<template>
    <v-container fluid class="free-agents-page">
        <div class="fa-header">
            <span class="fa-header__icon custom-orange">
                <v-icon color="white">mdi-account-search-outline</v-icon>
            </span>
            <div class="fa-header__text">
                <h1 class="fa-header__title">Free Agents</h1>
                <div class="fa-header__subtitle">Διαθέσιμοι παίκτες χωρίς ομάδα, με τα στατιστικά τους από όλους τους αγώνες</div>
            </div>
        </div>

        <!-- ---------- Filters ---------- -->
        <div class="fa-filters">
            <div class="fa-filters__field fa-filters__field--position">
                <div class="fa-filters__label">Θέση</div>
                <v-btn-toggle v-model="filters.position" mandatory class="fa-toggle">
                    <v-btn v-for="option in positionOptions" :key="option.value" :value="option.value" small class="fa-toggle__btn">
                        {{ option.text }}
                    </v-btn>
                </v-btn-toggle>
            </div>

            <div class="fa-filters__field">
                <div class="fa-filters__label">Ηλικία: {{ filters.age[0] }} - {{ filters.age[1] }}</div>
                <v-range-slider
                    v-model="filters.age"
                    :min="ageBounds[0]"
                    :max="ageBounds[1]"
                    color="orange"
                    track-color="grey lighten-2"
                    hide-details
                    class="mt-0"
                />
            </div>

            <div class="fa-filters__field">
                <div class="fa-filters__label">Ελάχιστο ύψος: {{ filters.minHeight ? `${filters.minHeight} cm` : 'Όλα' }}</div>
                <v-slider
                    v-model="filters.minHeight"
                    :min="0"
                    :max="heightBounds[1]"
                    :step="5"
                    color="orange"
                    track-color="grey lighten-2"
                    hide-details
                    class="mt-0"
                />
            </div>

            <div class="fa-filters__field">
                <v-autocomplete
                    v-model="filters.teamId"
                    :items="previousTeamOptions"
                    item-text="name"
                    item-value="id"
                    label="Έχει παίξει στην ομάδα"
                    color="orange"
                    item-color="orange"
                    prepend-inner-icon="mdi-account-group"
                    clearable
                    dense
                    outlined
                    hide-details
                />
            </div>

            <div class="fa-filters__field">
                <v-select
                    v-model="filters.sortBy"
                    :items="sortOptions"
                    label="Ταξινόμηση"
                    color="orange"
                    item-color="orange"
                    prepend-inner-icon="mdi-sort"
                    dense
                    outlined
                    hide-details
                />
            </div>

            <div class="fa-filters__field fa-filters__field--switch">
                <v-switch
                    v-model="filters.withStatsOnly"
                    label="Μόνο με αγώνες"
                    color="orange"
                    inset
                    hide-details
                    class="mt-0 pt-0"
                />
                <v-btn v-if="hasActiveFilters" text small color="red" class="fa-filters__reset" @click="resetFilters">
                    <v-icon small left>mdi-close</v-icon>
                    Καθαρισμός
                </v-btn>
            </div>

            <!-- inside the white card, so it reads over any background -->
            <div v-if="!loading" class="fa-count">
                <v-icon small color="orange darken-2">mdi-account-group-outline</v-icon>
                <b>{{ filteredAgents.length }}</b>
                {{ filteredAgents.length === 1 ? 'διαθέσιμος παίκτης' : 'διαθέσιμοι παίκτες' }}
            </div>
        </div>

        <!-- ---------- List ---------- -->
        <div class="fa-results">
            <div v-if="!loading && !filteredAgents.length" class="fa-empty">
                <v-icon x-large color="orange">mdi-account-search-outline</v-icon>
                <div>Δεν βρέθηκαν παίκτες με αυτά τα φίλτρα</div>
            </div>

            <v-data-table
                v-else
                :headers="headers"
                :items="filteredAgents"
                :loading="loading"
                :page.sync="page"
                :items-per-page.sync="itemsPerPage"
                :footer-props="{ 'items-per-page-options': [10, 20, 50, 100], 'items-per-page-text': 'Παίκτες ανά σελίδα', 'page-text': '{0}-{1} από {2}' }"
                loading-text="Φόρτωση..."
                disable-sort
                :mobile-breakpoint="0"
                class="fa-table no-global-table-hover elevation-1 rounded-lg"
                @click:row="openAgent"
            >
                <template v-slot:item.player="{ item }">
                    <div class="fa-player">
                        <span class="fa-thumb">
                            <v-img :src="item.image ? `${assetsUrl}/${item.image}` : defaultPlayerImg" contain class="fa-thumb__img" @error="item.image = null" />
                        </span>
                        <span class="fa-player__text">
                            <span class="fa-player__name">{{ item.surname }} {{ item.name }}</span>
                            <span v-if="item.position" class="fa-player__position">{{ positionLabel(item.position) }}</span>
                        </span>
                    </div>
                </template>
                <template v-slot:item.ageYears="{ item }">{{ item.ageYears !== null ? item.ageYears : '-' }}</template>
                <template v-slot:item.heightCm="{ item }">{{ item.heightCm ? `${item.heightCm} cm` : '-' }}</template>
                <template v-slot:item.teams="{ item }">
                    <div class="fa-teams">
                        <info-tip
                            v-for="team in item.previousTeams.slice(0, 3)"
                            :key="team.id"
                            :text="`${team.name}: ${team.games} ${team.games === 1 ? 'αγώνας' : 'αγώνες'}`"
                        >
                            <span class="fa-team-logo">
                                <v-img v-if="team.logo" :src="`${assetsUrl}/${team.logo}`" contain class="fa-team-logo__img" />
                                <v-icon v-else x-small color="orange">mdi-basketball</v-icon>
                            </span>
                        </info-tip>
                        <span v-if="item.previousTeams.length > 3" class="fa-teams__more">+{{ item.previousTeams.length - 3 }}</span>
                        <span v-if="!item.previousTeams.length" class="fa-muted">-</span>
                    </div>
                </template>
                <template v-for="stat in statKeys" v-slot:[`item.${stat}`]="{ item }">
                    <span :key="stat" class="fa-num">{{ item.games ? formatStat(item[stat]) : '-' }}</span>
                </template>
                <template v-slot:item.pir="{ item }">
                    <span v-if="item.games" class="fa-pir">{{ formatStat(item.pir) }}</span>
                    <span v-else class="fa-muted">-</span>
                </template>
                <template v-slot:item.lastMatchDatetime="{ item }">{{ formatDate(item.lastMatchDatetime) }}</template>
                <template v-slot:item.actions="{ item }">
                    <v-btn icon small title="Προβολή" @click.stop="openAgent(item)">
                        <v-icon small color="orange darken-2">mdi-eye-outline</v-icon>
                    </v-btn>
                </template>
            </v-data-table>
        </div>

        <!-- the card of a player, on a click of the row -->
        <v-dialog v-model="dialog" max-width="640" :fullscreen="$vuetify.breakpoint.xs">
            <free-agent-card v-if="selected" :agent="selected" @close="dialog = false" />
        </v-dialog>
    </v-container>
</template>

<script>
    import moment from 'moment'
    import playerService from '@/services/player'
    import FreeAgentCard from '@/components/FreeAgentCard'
    import { formatDate, formatStat, positionLabel } from '@/utils/freeAgents'

    const MIN_AGE = 5
    const MAX_AGE = 99

    const SORTERS = {
        pir: (a, b) => b.pir - a.pir,
        points: (a, b) => b.points - a.points,
        rebounds: (a, b) => b.rebounds - a.rebounds,
        assists: (a, b) => b.assists - a.assists,
        games: (a, b) => b.games - a.games,
        height: (a, b) => (b.heightCm || 0) - (a.heightCm || 0),
        youngest: (a, b) => (a.ageYears === null ? 1 : b.ageYears === null ? -1 : a.ageYears - b.ageYears),
        recent: (a, b) => (b.lastMatchTime || 0) - (a.lastMatchTime || 0),
        name: (a, b) => `${a.surname} ${a.name}`.localeCompare(`${b.surname} ${b.name}`, 'el')
    }

    function defaultFilters(ageBounds = [0, 99]) {
        return {
            position: 'all',
            age: [...ageBounds],
            minHeight: 0,
            teamId: null,
            sortBy: 'pir',
            withStatsOnly: false
        }
    }

    // The free agents in a table (filters, sort and pagination on the page); a row opens the card of the player
    export default {
        components: { FreeAgentCard },
        data() {
            return {
                agents: [],
                loading: false,
                page: 1,
                itemsPerPage: 20,
                dialog: false,
                selected: null,
                // the averages shown as plain numbers in the table
                statKeys: ['games', 'points', 'rebounds', 'assists'],
                filters: defaultFilters(),
                assetsUrl: 'https://storage.googleapis.com',
                defaultPlayerImg: require('@/assets/default_player.png'),
                positionOptions: [
                    { text: 'Όλες', value: 'all' },
                    { text: 'Guard', value: 'guard' },
                    { text: 'Forward', value: 'forward' },
                    { text: 'Center', value: 'center' }
                ],
                sortOptions: [
                    { text: 'PIR', value: 'pir' },
                    { text: 'Πόντοι', value: 'points' },
                    { text: 'Ριμπάουντ', value: 'rebounds' },
                    { text: 'Ασίστ', value: 'assists' },
                    { text: 'Αγώνες', value: 'games' },
                    { text: 'Ύψος', value: 'height' },
                    { text: 'Νεότεροι πρώτα', value: 'youngest' },
                    { text: 'Πιο πρόσφατος αγώνας', value: 'recent' },
                    { text: 'Αλφαβητικά', value: 'name' }
                ]
            }
        },
        computed: {
            headers() {
                const all = [
                    { text: 'Παίκτης', value: 'player' },
                    { text: 'Ηλικία', value: 'ageYears', align: 'center', wide: true },
                    { text: 'Ύψος', value: 'heightCm', align: 'center', wide: true },
                    { text: 'Ομάδες', value: 'teams', wide: true },
                    { text: 'ΑΓ', value: 'games', align: 'center' },
                    { text: 'ΠΟΝ', value: 'points', align: 'center' },
                    { text: 'ΡΙΜ', value: 'rebounds', align: 'center', wide: true },
                    { text: 'ΑΣ', value: 'assists', align: 'center', wide: true },
                    { text: 'PIR', value: 'pir', align: 'center' },
                    { text: 'Τελ. αγώνας', value: 'lastMatchDatetime', align: 'center', wide: true },
                    { text: '', value: 'actions', align: 'end', width: 48 }
                ]
                // a phone shows the essentials, the rest is on the card
                return this.$vuetify.breakpoint.xs ? all.filter(header => !header.wide) : all
            },
            ageBounds() {
                const ages = this.agents.map(agent => agent.ageYears).filter(age => age !== null)
                return ages.length ? [Math.min(...ages), Math.max(...ages)] : [0, 99]
            },
            heightBounds() {
                const heights = this.agents.map(agent => agent.heightCm).filter(Boolean)
                return heights.length ? [Math.min(...heights), Math.ceil(Math.max(...heights) / 5) * 5] : [0, 230]
            },
            previousTeamOptions() {
                const byId = {}
                this.agents.forEach(agent => agent.previousTeams.forEach(team => { byId[team.id] = team }))
                return Object.values(byId).sort((a, b) => a.name.localeCompare(b.name, 'el'))
            },
            hasActiveFilters() {
                const defaults = defaultFilters(this.ageBounds)
                return Object.keys(defaults).some(key => JSON.stringify(defaults[key]) !== JSON.stringify(this.filters[key]))
            },
            filteredAgents() {
                const { position, age, minHeight, teamId, sortBy, withStatsOnly } = this.filters
                const ageFilterActive = age[0] !== this.ageBounds[0] || age[1] !== this.ageBounds[1]

                return this.agents
                    .filter(agent => position === 'all' || agent.position === position)
                    .filter(agent => !ageFilterActive || (agent.ageYears !== null && agent.ageYears >= age[0] && agent.ageYears <= age[1]))
                    .filter(agent => !minHeight || (agent.heightCm && agent.heightCm >= minHeight))
                    .filter(agent => !teamId || agent.previousTeams.some(team => team.id === teamId))
                    .filter(agent => !withStatsOnly || agent.games > 0)
                    .sort(SORTERS[sortBy] || SORTERS.pir)
            }
        },
        watch: {
            // other filters or sort: back to the first page
            filters: {
                deep: true,
                handler() {
                    this.page = 1
                }
            }
        },
        created() {
            this.fetchFreeAgents()
        },
        methods: {
            fetchFreeAgents() {
                this.loading = true
                playerService.getFreeAgents().then(resp => {
                    this.agents = resp.data.map(agent => this.normalize(agent))
                    this.filters = defaultFilters(this.ageBounds)
                }).finally(() => {
                    this.loading = false
                })
            },
            normalize(agent) {
                const birthday = agent.birthday ? moment(agent.birthday, 'DD/MM/YYYY') : null
                const ageYears = birthday && birthday.isValid() ? moment().diff(birthday, 'years') : null
                return {
                    ...agent,
                    previousTeams: agent.previousTeams || [],
                    // a wrong birthday (e.g. in the future) would stretch the age filter: no age then
                    ageYears: ageYears !== null && ageYears >= MIN_AGE && ageYears <= MAX_AGE ? ageYears : null,
                    heightCm: this.toCm(agent.height),
                    lastMatchTime: agent.lastMatchDatetime ? moment(agent.lastMatchDatetime).valueOf() : null
                }
            },
            // Heights are stored either in metres (1.85) or centimetres (185)
            toCm(height) {
                const value = Number(height)
                if (!value) return null
                return Math.round(value < 3 ? value * 100 : value)
            },
            formatDate,
            formatStat,
            positionLabel,
            resetFilters() {
                this.filters = defaultFilters(this.ageBounds)
            },
            openAgent(agent) {
                this.selected = agent
                this.dialog = true
            }
        }
    }
</script>

<style scoped>
.free-agents-page {
    max-width: 1200px;
    margin: 0 auto;
    font-family: Ubuntu, Arial, sans-serif;
    text-align: left;
}

/* ---------- Header ---------- */
.fa-header {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 16px 20px;
    background: #fff;
    border-radius: 12px 12px 0 0;
    border-top: 4px solid #ff7133;
}

.fa-header__icon {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 10px;
}

.fa-header__title {
    font-family: Mansalva, Arial, sans-serif;
    font-size: 1.9rem;
    font-weight: 800;
    line-height: 1.2;
}

.fa-header__subtitle {
    color: rgba(0, 0, 0, 0.55);
    font-size: 0.95rem;
}

/* ---------- Filters ---------- */
.fa-filters {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 14px 20px;
    padding: 6px 20px 18px;
    background: #fff;
    border-radius: 0 0 12px 12px;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.1);
}

.fa-filters__label {
    margin-bottom: 4px;
    color: rgba(0, 0, 0, 0.6);
    font-size: 0.8rem;
    font-weight: 700;
}

.fa-filters__field--switch {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}

.fa-filters__reset {
    font-weight: 700;
    text-transform: none;
    letter-spacing: normal;
}

.fa-toggle {
    background: #0d0d0f !important;
    border-radius: 8px !important;
    padding: 3px;
}

.fa-toggle .fa-toggle__btn {
    background: transparent !important;
    border: none !important;
    border-radius: 6px !important;
    color: rgba(255, 255, 255, 0.75) !important;
    font-weight: 700;
    text-transform: none;
    letter-spacing: normal;
    height: 30px !important;
}

.fa-toggle .fa-toggle__btn.v-btn--active {
    background: linear-gradient(90deg, #ef4444 0%, #ff7133 50%, orange 100%) !important;
    color: #fff !important;
}

.fa-toggle .fa-toggle__btn.v-btn--active::before {
    opacity: 0 !important;
}

.fa-count {
    display: flex;
    grid-column: 1 / -1;
    align-items: center;
    gap: 6px;
    padding-top: 12px;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
    color: rgba(0, 0, 0, 0.6);
    font-size: 0.9rem;
    font-weight: 600;
}

.fa-count b {
    color: #111;
    font-size: 1rem;
}

/* ---------- Table (same look as the standings / penalties) ---------- */

/* apart from the filters card, not stuck to it */
.fa-results {
    margin-top: 16px;
}

.fa-table {
    overflow: hidden;
}

.fa-table ::v-deep .v-data-table__wrapper table > thead > tr > th {
    background: #0d0d0f !important;
    color: #fff !important;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    white-space: nowrap;
    border-bottom: 3px solid #ff7133 !important;
}

.fa-table ::v-deep .v-data-table__wrapper table > tbody > tr {
    cursor: pointer;
}

.fa-table ::v-deep .v-data-table__wrapper table > tbody > tr > td {
    height: 56px;
    background: #fff;
}

.fa-table ::v-deep .v-data-table__wrapper table > tbody > tr:nth-child(even) > td {
    background: #f7f7f8 !important;
}

.fa-table ::v-deep .v-data-table__wrapper table > tbody > tr:hover > td {
    background: #fff3ec !important;
}

.fa-table ::v-deep .v-data-table__wrapper table > tbody > tr:hover > td:first-child {
    box-shadow: inset 4px 0 0 #ff7133;
}

.fa-player {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 160px;
}

.fa-thumb {
    display: inline-flex;
    flex: 0 0 auto;
    width: 38px;
    height: 38px;
    padding: 2px;
    border-radius: 8px;
    background: linear-gradient(180deg, #f1f1f3, #e4e4e8);
    overflow: hidden;
}

.fa-thumb__img {
    width: 100%;
    height: 100%;
}

.fa-player__text {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.fa-player__name {
    font-weight: 700;
    line-height: 1.2;
}

.fa-player__position {
    color: rgba(0, 0, 0, 0.5);
    font-size: 0.75rem;
    font-weight: 600;
}

.fa-teams {
    display: flex;
    align-items: center;
    gap: 4px;
}

.fa-team-logo {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #fff;
    border: 1px solid rgba(0, 0, 0, 0.08);
    overflow: hidden;
}

.fa-team-logo__img {
    width: 100%;
    height: 100%;
}

.fa-teams__more {
    color: rgba(0, 0, 0, 0.55);
    font-size: 0.75rem;
    font-weight: 700;
}

.fa-num {
    font-weight: 600;
    font-variant-numeric: tabular-nums;
}

.fa-pir {
    display: inline-block;
    min-width: 40px;
    padding: 2px 8px;
    border-radius: 6px;
    background: linear-gradient(90deg, #ef4444 0%, #ff7133 50%, orange 100%);
    color: #fff;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
}

.fa-muted {
    color: rgba(0, 0, 0, 0.45);
}

.fa-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 40px 16px;
    background: #fff;
    border-radius: 12px;
    color: rgba(0, 0, 0, 0.6);
    font-weight: 600;
    text-align: center;
}

/* ---------- Responsive ---------- */
@media (max-width: 960px) {
    .fa-filters {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (max-width: 600px) {
    .fa-filters {
        grid-template-columns: minmax(0, 1fr);
        padding: 6px 14px 14px;
    }

    .fa-header__title {
        font-size: 1.45rem;
    }

    .fa-toggle {
        display: flex;
    }

    .fa-toggle .fa-toggle__btn {
        flex: 1 1 0;
        min-width: 0 !important;
    }

    /* a phone shows fewer columns (see headers): they fit without a horizontal scroll */
    .fa-table ::v-deep .v-data-table__wrapper table > thead > tr > th,
    .fa-table ::v-deep .v-data-table__wrapper table > tbody > tr > td {
        padding: 0 6px !important;
    }

    .fa-table ::v-deep .v-data-table__wrapper table > thead > tr > th:first-child,
    .fa-table ::v-deep .v-data-table__wrapper table > tbody > tr > td:first-child {
        padding-left: 10px !important;
    }

    .fa-player {
        min-width: 0;
        gap: 8px;
    }

    .fa-thumb {
        width: 30px;
        height: 30px;
    }

    .fa-player__name {
        font-size: 0.85rem;
    }

    .fa-pir {
        min-width: 34px;
        padding: 2px 6px;
    }
}
</style>
